import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { getCompany } from '@/lib/company';
import {
  SquaresFour, Receipt, Users, Wrench, Wallet, ChartBar, SignOut, Fingerprint, UsersThree, Hourglass, List, X, Gear, ArrowsLeftRight, ArrowDown, ArrowUp, Scales, NotePencil, ArrowsClockwise, Briefcase, Clock, ShieldCheck, CalendarCheck, AirplaneTilt, Trophy, Gift, FileText, Notebook, CaretDown, ArrowUUpLeft, CreditCard
} from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import KeyboardShortcuts from '@/components/KeyboardShortcuts';
import SidebarThemePicker from '@/components/SidebarThemePicker';
import TrialBanner from '@/components/TrialBanner';
import { QuickStartDialog, useQuickStartAutoOpen, hasUnseenUpdates } from '@/components/QuickStartGuide';
import { BookOpen } from '@phosphor-icons/react';
import { ROUTE_TO_PAGE, can } from '@/lib/permissions';
import { resolveSidebarBg, isDarkBg } from '@/lib/sidebarThemes';
import { getCurrentFY } from '@/lib/fy';
import { toast } from 'sonner';

// Each nav item carries a brand color for the colorful icon look.
// `shortcut` matches its keyboard combo (Ctrl+Shift+<letter>); the matching letter
// in the label is highlighted at render time.
// Groups are collapsible — the open/closed state is persisted per-user in
// localStorage. By default only "Operations" is expanded so a fresh staff /
// admin sees a compact menu and drills into what they need.
const navSections = [
  {
    key: 'operations',
    title: 'OPERATIONS',
    headerCls: 'text-[#2563EB]',          // Brand blue — daily work flow.
    headerBg: 'bg-[#DBEAFE]',
    defaultOpen: true,
    items: [
      { to: '/invoices',   label: 'New Invoice/Token', icon: Receipt,         bg: 'bg-[#E0E7FF]', text: 'text-[#4F46E5]', shortcut: 'N', countKey: 'tokens' },
      { to: '/my-work',    label: 'My Work',           icon: Briefcase,       bg: 'bg-[#EDE9FE]', text: 'text-[#6D28D9]', shortcut: 'W', countKey: 'my_work' },
      { to: '/processing', label: 'Follow-Up',         icon: Hourglass,       bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', shortcut: 'F', countKey: 'processing' },
      { to: '/recurring',  label: 'Recurring',         icon: ArrowsClockwise, bg: 'bg-[#E0F2FE]', text: 'text-[#0284C7]', shortcut: 'U', countKey: 'recurring' },
      { to: '/notes',      label: 'To-Do Notes',       icon: NotePencil,      bg: 'bg-[#EDE9FE]', text: 'text-[#7C3AED]', shortcut: 'O', countKey: 'notes' },
    ]
  },
  {
    key: 'finance',
    title: 'FINANCE',
    headerCls: 'text-[#10B981]',          // Green — money / cash.
    headerBg: 'bg-[#DCFCE7]',
    defaultOpen: false,
    items: [
      { to: '/receipt',  label: 'Receipt',  icon: ArrowDown,       bg: 'bg-[#DCFCE7]', text: 'text-[#10B981]', shortcut: 'R' },
      { to: '/refund',   label: 'Refund',   icon: ArrowUUpLeft,    bg: 'bg-[#FEE2E2]', text: 'text-[#EF4444]' },
      { to: '/income',   label: 'Income',   icon: ArrowUp,         bg: 'bg-[#DCFCE7]', text: 'text-[#10B981]', shortcut: 'I' },
      { to: '/expense',  label: 'Payment',  icon: ArrowDown,       bg: 'bg-[#FEE2E2]', text: 'text-[#EF4444]', shortcut: 'E' },
      { to: '/transfer', label: 'Transfer', icon: ArrowsLeftRight, bg: 'bg-[#FFFBEB]', text: 'text-[#F59E0B]', shortcut: 'T' },
    ]
  },
  {
    key: 'masters',
    title: 'MASTERS',
    headerCls: 'text-[#DB2777]',          // Pink — entities / reference data.
    headerBg: 'bg-[#FCE7F3]',
    defaultOpen: false,
    items: [
      { to: '/customers',       label: 'Customer',        icon: Users,       bg: 'bg-[#FCE7F3]', text: 'text-[#DB2777]', shortcut: 'C' },
      { to: '/customer-groups', label: 'Customer Groups', icon: UsersThree,  bg: 'bg-[#DBEAFE]', text: 'text-[#1D4ED8]' },
      { to: '/services',        label: 'Services',        icon: Wrench,      bg: 'bg-[#EDE9FE]', text: 'text-[#7C3AED]', shortcut: 'S' },
      { to: '/service-groups',  label: 'Service Groups',  icon: UsersThree,  bg: 'bg-[#DBEAFE]', text: 'text-[#1D4ED8]' },
    ]
  },
  {
    key: 'analytics',
    title: 'ANALYTICS',
    headerCls: 'text-[#0D9488]',          // Teal — reports / numbers.
    headerBg: 'bg-[#CCFBF1]',
    defaultOpen: false,
    items: [
      { to: '/reports',       label: 'Reports',                  icon: ChartBar, bg: 'bg-[#CCFBF1]', text: 'text-[#0D9488]', shortcut: 'R' },
      { to: '/reports/daily', label: 'Daily Transaction Report', icon: Notebook, bg: 'bg-[#E0F2FE]', text: 'text-[#0369A1]' },
    ]
  },
  {
    key: 'staff_management',
    title: 'STAFF MANAGEMENT',
    headerCls: 'text-[#E11D48]',          // Rose — people / HR.
    headerBg: 'bg-[#FFE4E6]',
    defaultOpen: false,
    items: [
      { to: '/staff',             label: 'Staff List',      icon: UsersThree,     bg: 'bg-[#FFE4E6]', text: 'text-[#E11D48]' },
      { to: '/staff/attendance',  label: 'Attendance',      icon: CalendarCheck,  bg: 'bg-[#DCFCE7]', text: 'text-[#15803D]' },
      { to: '/staff/leave',       label: 'Leave',           icon: AirplaneTilt,   bg: 'bg-[#EDE9FE]', text: 'text-[#6D28D9]' },
      { to: '/staff/salary',      label: 'Salary',          icon: Wallet,         bg: 'bg-[#FFFBEB]', text: 'text-[#D97706]' },
      { to: '/staff/performance', label: 'Performance',     icon: Trophy,         bg: 'bg-[#FFFBEB]', text: 'text-[#F59E0B]' },
      { to: '/staff/work-log',    label: 'Work Log',        icon: NotePencil,     bg: 'bg-[#FEF3C7]', text: 'text-[#92400E]' },
      { to: '/staff/incentives',  label: 'Incentives',      icon: Gift,           bg: 'bg-[#FCE7F3]', text: 'text-[#BE185D]' },
      { to: '/staff/reports',     label: 'Reports',         icon: FileText,       bg: 'bg-[#E0F2FE]', text: 'text-[#0369A1]' },
      { to: '/staff/shifts',      label: 'Shift Settings',  icon: Clock,          bg: 'bg-[#E0F2FE]', text: 'text-[#0284C7]' },
      { to: '/staff/permissions', label: 'Permissions',     icon: ShieldCheck,    bg: 'bg-[#E0E7FF]', text: 'text-[#4338CA]' },
    ]
  },
  {
    key: 'management',
    title: 'MANAGEMENT',
    headerCls: 'text-[#52525B]',          // Slate — settings / admin.
    headerBg: 'bg-[#F4F4F5]',
    defaultOpen: false,
    items: [
      { to: '/settings', label: 'Settings', icon: Gear, bg: 'bg-[#F4F4F5]', text: 'text-[#52525B]' },
      { to: '/billing', label: 'Plan & Billing', icon: CreditCard, bg: 'bg-[#E0E7FF]', text: 'text-[#4F46E5]' },
    ]
  }
];

export default function Layout() {
  const { user, logout, permissions, updateMyPreferences } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard' || location.pathname === '/';
  const isAdmin = user?.role === 'admin';
  const qsKey = `admin_${user?.id || user?.email || ''}`;
  const [guideOpen, setGuideOpen] = useQuickStartAutoOpen(qsKey, isAdmin);

  // Page metadata for the sticky-top heading area. Each entry maps a route to its
  // icon (reuses the sidebar nav icon + colours), heading and tagline. Centralised
  // here so individual pages stop carrying their own H1/tagline copy.
  const PAGE_META = {
    '/dashboard':  { icon: SquaresFour,    bg: 'bg-[#DBEAFE]', text: 'text-[#2563EB]', title: 'Dashboard',         tagline: "Today's snapshot of revenue, work and follow-ups" },
    '/invoices':   { icon: Receipt,        bg: 'bg-[#E0E7FF]', text: 'text-[#4F46E5]', title: 'Invoice Management', tagline: 'Create and track all CSC service invoices' },
    '/transfer':   { icon: ArrowsLeftRight,bg: 'bg-[#FFFBEB]', text: 'text-[#F59E0B]', title: 'Transfer Funds',    tagline: 'Move money between your own accounts (cash, bank, wallet)' },
    '/receipt':    { icon: ArrowDown,      bg: 'bg-[#DCFCE7]', text: 'text-[#10B981]', title: 'Receipt',           tagline: 'Record money received from a customer — adjust against open bills or hold as advance' },
    '/refund':     { icon: ArrowUUpLeft,   bg: 'bg-[#FEE2E2]', text: 'text-[#EF4444]', title: 'Refund Advance',    tagline: 'Return a portion of a customer\u2019s advance back to them as cash, bank, or wallet' },
    '/expense':    { icon: ArrowDown,      bg: 'bg-[#FEE2E2]', text: 'text-[#EF4444]', title: 'Payment',    tagline: 'Pay expenses, creditors or capital drawings from Cash / Bank' },
    '/income':     { icon: ArrowUp,        bg: 'bg-[#DCFCE7]', text: 'text-[#10B981]', title: 'Record Income',     tagline: 'Log money received from sources outside the invoicing flow' },
    '/processing': { icon: Hourglass,      bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', title: 'Follow-Up',         tagline: 'Service items that require follow-up processing' },
    '/recurring':  { icon: ArrowsClockwise,bg: 'bg-[#E0F2FE]', text: 'text-[#0284C7]', title: 'Recurring',         tagline: 'Subscription-style services and their upcoming renewals' },
    '/my-work':    { icon: Briefcase,      bg: 'bg-[#EDE9FE]', text: 'text-[#6D28D9]', title: 'My Work',           tagline: 'Invoice items currently assigned to you' },
    '/notes':      { icon: NotePencil,     bg: 'bg-[#EDE9FE]', text: 'text-[#7C3AED]', title: 'To-Do Notes',       tagline: 'Track important reminders, tasks and follow-ups for your team' },
    '/customers':       { icon: Users,          bg: 'bg-[#FCE7F3]', text: 'text-[#DB2777]', title: 'Customers',         tagline: 'Manage customer records' },
    '/customer-groups': { icon: UsersThree,     bg: 'bg-[#DBEAFE]', text: 'text-[#1D4ED8]', title: 'Customer Groups',   tagline: 'Tag customers by segment for easier filtering and reporting' },
    '/services':   { icon: Wrench,         bg: 'bg-[#EDE9FE]', text: 'text-[#7C3AED]', title: 'Services',          tagline: 'Manage service catalog' },
    '/service-groups': { icon: UsersThree, bg: 'bg-[#DBEAFE]', text: 'text-[#1D4ED8]', title: 'Service Groups',    tagline: 'Bundle services by portal — links auto-fill every member service' },
    '/reports':    { icon: ChartBar,       bg: 'bg-[#CCFBF1]', text: 'text-[#0D9488]', title: 'Reports',           tagline: 'Balance sheet, day book & financial overview' },
    '/staff':             { icon: UsersThree,     bg: 'bg-[#FFE4E6]', text: 'text-[#E11D48]', title: 'Staff List',         tagline: 'Manage team members, profiles and roles' },
    '/staff/attendance':  { icon: CalendarCheck,  bg: 'bg-[#DCFCE7]', text: 'text-[#15803D]', title: 'Attendance',         tagline: 'Daily check-in / check-out, status & audit history' },
    '/staff/leave':       { icon: AirplaneTilt,   bg: 'bg-[#EDE9FE]', text: 'text-[#6D28D9]', title: 'Leave Management',   tagline: 'Casual, sick, emergency and unpaid leave — request and approval' },
    '/staff/salary':      { icon: Wallet,         bg: 'bg-[#FFFBEB]', text: 'text-[#D97706]', title: 'Salary',             tagline: 'Auto-calculated monthly salary from attendance with printable slip' },
    '/staff/performance': { icon: Trophy,         bg: 'bg-[#FFFBEB]', text: 'text-[#F59E0B]', title: 'Staff Performance',  tagline: 'Productivity metrics and weighted performance score per staff' },
    '/staff/work-log':    { icon: NotePencil,     bg: 'bg-[#FEF3C7]', text: 'text-[#92400E]', title: 'Daily Work Log',     tagline: 'Five-field daily journal — completed, pending, follow-ups, issues, tomorrow\'s plan' },
    '/staff/shifts':      { icon: Clock,          bg: 'bg-[#E0F2FE]', text: 'text-[#0284C7]', title: 'Shift Settings',     tagline: 'Per-staff timing, grace, weekly offs and paid leave limits' },
    '/staff/permissions': { icon: ShieldCheck,    bg: 'bg-[#E0E7FF]', text: 'text-[#4338CA]', title: 'Permissions',        tagline: 'Toggle View / Edit / Delete rights per staff and per module' },
    '/settings':          { icon: Gear,           bg: 'bg-[#F4F4F5]', text: 'text-[#52525B]', title: 'Settings',           tagline: 'Application preferences and configuration' },
    '/billing':           { icon: CreditCard,     bg: 'bg-[#E0E7FF]', text: 'text-[#4F46E5]', title: 'Plan & Billing',     tagline: 'Your subscription, upgrades and payment history' },
  };
  const currentPage = (() => {
    const p = location.pathname;
    if (PAGE_META[p]) return PAGE_META[p];
    // Deep-link variants
    if (p.startsWith('/invoices/edit/')) return { ...PAGE_META['/invoices'], title: 'Edit Invoice', tagline: 'Update invoice details and re-allocate payment' };
    if (p.startsWith('/invoices/'))      return { ...PAGE_META['/invoices'], title: 'New Invoice', tagline: 'Create a new CSC service invoice' };
    if (p === '/' )                      return PAGE_META['/dashboard'];
    return null;
  })();

  // Sidebar theme — resolved from the user's stored sidebar_bg preference.
  const sidebarBg = resolveSidebarBg(user?.sidebar_bg);
  const dark = isDarkBg(sidebarBg);

  // Tailwind class shortcuts that flip to white-on-dark when a dark theme is active.
  // Keep colored brand-icons + bold shortcut letters intact (they read fine on dark too).
  const navInactiveText = dark ? 'text-white/70' : 'text-[#52525B]';
  const navHoverBg = dark ? 'hover:bg-white/10 hover:text-white' : 'hover:bg-[#F4F4F5] hover:text-[#0A0A0A]';
  const navActiveCls = dark ? 'bg-white/15 text-white font-medium' : 'bg-[#3B82F6]/10 text-[#0A0A0A] font-medium';
  const sectionTitleText = dark ? 'text-white/40' : 'text-[#A1A1AA]';
  const headerBorder = dark ? 'border-white/10' : 'border-[#E4E4E7]';
  const companyNameText = dark ? 'text-white' : 'text-[#0A0A0A]';
  const mobileCloseText = dark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-[#A1A1AA] hover:text-[#0A0A0A] hover:bg-[#F4F4F5]';

  const handleThemeChange = async (themeId) => {
    try {
      await updateMyPreferences({ sidebar_bg: themeId });
      toast.success('Sidebar theme updated');
    } catch {
      toast.error('Failed to save theme');
    }
  };

  // Filter nav sections so staff only see what they're permitted to view.
  // Admins always see everything. The Staffs/Settings group is admin-only.
  const filterItems = (items) => items.filter(item => {
    // Admin-only items: Staff List, Shift Settings, Permissions, Settings.
    if (item.to === '/staff' || item.to === '/settings' || item.to === '/billing'
        || item.to === '/staff/shifts' || item.to === '/staff/permissions') return isAdmin;
    if (isAdmin) return true;
    const pageKey = ROUTE_TO_PAGE[item.to];
    if (!pageKey) return true; // route not in matrix → keep visible
    // `__self__` sentinel (e.g. /my-work) — always visible to logged-in users.
    if (pageKey === '__self__') return true;
    return can(permissions, pageKey, 'view');
  });
  const visibleSections = navSections
    .map(s => ({ ...s, items: filterItems(s.items) }))
    .filter(s => s.items.length > 0)
    // Staff-panel ordering tweak — bring "My Work" to the top of OPERATIONS
    // since it's a staff member's primary daily entry point.
    .map(s => {
      if (s.key !== 'operations' || user?.role !== 'staff') return s;
      const idx = s.items.findIndex(it => it.to === '/my-work');
      if (idx <= 0) return s;
      const items = [...s.items];
      const [mw] = items.splice(idx, 1);
      items.unshift(mw);
      return { ...s, items };
    });
  const [counts, setCounts] = useState({ processing: 0, notes: 0 });
  const [company, setCompany] = useState({ name: '', logo: '' });
  const navRef = useRef(null);
  // Collapsible nav groups. Each section starts open per its `defaultOpen` flag,
  // overridden by per-user localStorage. Clicking the group header toggles it.
  // The group containing the current route is auto-opened so navigation never
  // hides where you are.
  const [openGroups, setOpenGroups] = useState(() => {
    // First-load rule: if landing on Dashboard, start with all groups closed
    // (overrides any stored state). Otherwise honor localStorage, falling
    // back to "only the default-open group expanded".
    if (typeof window !== 'undefined' && (window.location.pathname === '/dashboard' || window.location.pathname.startsWith('/dashboard/'))) {
      return {};
    }
    try {
      const stored = JSON.parse(localStorage.getItem('sidebar-open-groups') || 'null');
      if (stored && typeof stored === 'object') return stored;
    } catch (_) { /* ignore */ }
    const def = navSections.find(s => s.defaultOpen);
    return def ? { [def.key]: true } : {};
  });
  useEffect(() => {
    // When the user navigates to the Dashboard, close every collapsible
    // group so the menu shows a clean, minimal sidebar.
    if (location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/')) {
      if (Object.values(openGroups).some(Boolean)) {
        setOpenGroups({});
        try { localStorage.setItem('sidebar-open-groups', JSON.stringify({})); } catch (_) { /* ignore */ }
      }
      return;
    }
    // Accordion auto-open: when the route changes, close every other group
    // and open just the one containing the new route.
    const active = navSections.find(s => s.items.some(it => location.pathname === it.to || location.pathname.startsWith(it.to + '/')));
    if (active && !openGroups[active.key]) {
      const next = { [active.key]: true };
      setOpenGroups(next);
      try { localStorage.setItem('sidebar-open-groups', JSON.stringify(next)); } catch (_) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const toggleGroup = (k) => {
    setOpenGroups(prev => {
      // Accordion behavior — clicking an open group collapses it; clicking a
      // closed group opens it and closes every other group.
      const next = prev[k] ? {} : { [k]: true };
      try { localStorage.setItem('sidebar-open-groups', JSON.stringify(next)); } catch (_) { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    const reload = () => {
      // Re-fetch on demand (cache is cleared by SettingsPage before this fires)
      getCompany().then(c => setCompany({ name: c.name || '', logo: c.logo || '' })).catch(() => {});
    };
    reload();
    window.addEventListener('company-updated', reload);
    return () => window.removeEventListener('company-updated', reload);
  }, []);

  // Sync the browser tab title with the company name. Falls back to "SMSuitePro"
  // when the admin hasn't set a company name yet.
  useEffect(() => {
    document.title = company.name ? company.name : 'SMSuitePro';
  }, [company.name]);

  // Two independent states: desktop collapsed (icon-rail) vs mobile drawer open.
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { localStorage.setItem('sidebarCollapsed', String(desktopCollapsed)); }, [desktopCollapsed]);

  useEffect(() => {
    const fetchCounts = () => {
      api.get('/processing/count').then(r => setCounts(c => ({ ...c, processing: r.data.count }))).catch(() => {});
      api.get('/notes/count').then(r => setCounts(c => ({ ...c, notes: r.data.count }))).catch(() => {});
      api.get('/tokens/count').then(r => setCounts(c => ({ ...c, tokens: r.data.count }))).catch(() => {});
      // Recurring count = "nearest dues" sliding window: last 30 days for
      // yearly, 15 days for half-yearly, 7 days for quarterly/custom — plus
      // anything already overdue. Computed client-side from the full list
      // so we don't add another backend endpoint just for a count.
      api.get('/recurring').then(r => {
        const rows = r.data || [];
        const count = rows.filter(row => {
          const d = row.days_until_due;
          if (d === null || d === undefined) return false;
          if (d < 0) return true; // overdue always counts
          const interval = row.recurring_interval;
          // Per-interval intimation windows:
          //   yearly        → 30 days before due
          //   half_yearly   → 15 days
          //   quarterly     → 7 days
          //   monthly/custom → 5 days
          let windowDays = 7;
          if (interval === 'yearly') windowDays = 30;
          else if (interval === 'half_yearly') windowDays = 15;
          else if (interval === 'quarterly') windowDays = 7;
          else if (interval === 'monthly') windowDays = 5;
          else windowDays = 5; // custom + anything else
          return d <= windowDays;
        }).length;
        setCounts(c => ({ ...c, recurring: count }));
      }).catch(() => {});
      api.get('/work/my-work/count').then(r => setCounts(c => ({ ...c, my_work: r.data.count }))).catch(() => {});
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setMobileOpen(o => !o);
    } else {
      setDesktopCollapsed(c => !c);
    }
  };

  const isCollapsed = desktopCollapsed; // desktop rail mode
  const railWidth = 'lg:w-16';
  const fullWidth = 'lg:w-60';

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex h-screen overflow-hidden bg-[#F8FAFC]" data-testid="app-layout">
        {/* Mobile backdrop */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-backdrop"
          />
        )}

        <aside
          className={`fixed lg:static top-0 bottom-0 left-0 z-40 border-r ${headerBorder} flex flex-col shrink-0 transition-all duration-300 w-60 ${isCollapsed ? railWidth : fullWidth} ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
          style={{ backgroundColor: sidebarBg }}
          data-testid="sidebar"
        >
          {/* Header */}
          <div className={`p-3 border-b ${headerBorder}`}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden bg-[#3B82F6]">
                {company.logo ? (
                  <img src={company.logo} alt="logo" className="w-full h-full object-contain bg-white" />
                ) : (
                  <Fingerprint size={16} weight="bold" className="text-white" />
                )}
              </div>
              <span className={`text-sm font-bold tracking-tight ${companyNameText} flex-1 truncate ${isCollapsed ? 'lg:hidden' : ''}`} style={{ fontFamily: 'var(--font-heading)' }} data-testid="sidebar-company-name">
                {company.name || 'SMSuitePro'}
              </span>
              {/* Mobile close */}
              <button
                onClick={() => setMobileOpen(false)}
                className={`p-1 rounded-md lg:hidden ${mobileCloseText}`}
                aria-label="Close sidebar"
                data-testid="close-sidebar-btn"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Nav */}
          <nav
            className="flex-1 py-2 px-2 overflow-y-auto"
            ref={navRef}
            onKeyDown={(e) => {
              // Sidebar keyboard navigation.
              //   Escape  → collapse every expanded group + blur focus.
              //   ↓ / ↑   → move focus to the next / previous focusable nav
              //             entry (Dashboard link, group header buttons,
              //             and item links — but only those visible right
              //             now, so collapsed groups' children are skipped).
              if (e.key === 'Escape') {
                e.preventDefault();
                // Two-step Escape:
                //   1st press → if focus is on a nav item inside a group,
                //               jump focus up to that group's header.
                //   2nd press → focus the Dashboard link AND collapse every
                //               expanded group so the sidebar returns to its
                //               clean default state.
                const nav = navRef.current;
                const focused = document.activeElement;
                const inGroupItem = focused
                  && nav?.contains(focused)
                  && focused.tagName === 'A'
                  && focused.getAttribute('data-testid')?.startsWith('nav-')
                  && !focused.getAttribute('data-testid')?.startsWith('nav-group-')
                  && focused.getAttribute('data-testid') !== 'nav-dashboard';
                if (inGroupItem) {
                  // Walk up to the wrapping section <div> that owns the
                  // group-header button, then focus that button.
                  let el = focused.parentElement;
                  while (el && !el.querySelector?.('[data-testid^="nav-group-"]')) {
                    el = el.parentElement;
                  }
                  const header = el?.querySelector('[data-testid^="nav-group-"]');
                  if (header) { header.focus(); return; }
                }
                // 2nd-step (or no group context) — collapse + focus Dashboard.
                if (Object.values(openGroups).some(Boolean)) {
                  setOpenGroups({});
                  try { localStorage.setItem('sidebar-open-groups', JSON.stringify({})); } catch (_) { /* ignore */ }
                }
                const dash = nav?.querySelector('[data-testid="nav-dashboard"]');
                if (dash) dash.focus();
                return;
              }
              if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
              const nav = navRef.current;
              if (!nav) return;
              const focusables = Array.from(
                nav.querySelectorAll('a[data-testid^="nav-"], button[data-testid^="nav-group-"]')
              ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
              if (!focusables.length) return;
              e.preventDefault();
              const current = focusables.indexOf(document.activeElement);
              const next = e.key === 'ArrowDown'
                ? (current < 0 ? 0 : (current + 1) % focusables.length)
                : (current <= 0 ? focusables.length - 1 : current - 1);
              focusables[next].focus();
            }}
          >
            {/* Standalone Dashboard — always visible at the top, outside any
                collapsible group. Mirrors the same item shape used inside
                navSections so it picks up active styling and tooltip on the
                collapsed rail. */}
            {(() => {
              const item = { to: '/dashboard', label: 'Dashboard', icon: SquaresFour, bg: 'bg-[#DBEAFE]', text: 'text-[#2563EB]', shortcut: 'D' };
              const link = (
                <NavLink
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid="nav-dashboard"
                  className={({ isActive }) =>
                    `relative flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm transition-all duration-200 group ${
                      isActive ? navActiveCls : `${navInactiveText} ${navHoverBg}`
                    } ${isCollapsed ? 'lg:justify-center' : ''}`
                  }
                >
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${item.bg}`}>
                    <item.icon size={15} weight="duotone" className={item.text} />
                  </span>
                  <span className={`flex-1 truncate ${isCollapsed ? 'lg:hidden' : ''}`}>
                    <span className={`font-bold ${item.text}`}>D</span>ashboard
                  </span>
                </NavLink>
              );
              return (
                <div className="mb-2 pb-2 border-b border-[#E4E4E7]">
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{link}</TooltipTrigger>
                      <TooltipContent side="right" className="bg-[#0A0A0A] text-white border-0 text-xs rounded-md">Dashboard</TooltipContent>
                    </Tooltip>
                  ) : link}
                </div>
              );
            })()}
            {visibleSections.map((section, sIdx) => {
              const open = !!openGroups[section.key];
              return (
                <div key={section.title} className={sIdx > 0 ? 'mt-2' : ''}>
                  {isCollapsed ? null : (
                    <button
                      type="button"
                      onClick={() => toggleGroup(section.key)}
                      data-testid={`nav-group-${section.key}`}
                      className={`w-full flex items-center justify-between px-2.5 py-1.5 mb-1 rounded-md ${section.headerBg || ''} hover:brightness-95 transition-all`}
                    >
                      <span className={`text-[12px] uppercase tracking-[0.12em] font-bold ${section.headerCls || sectionTitleText}`}>{section.title}</span>
                      <CaretDown size={12} weight="bold" className={`${section.headerCls || sectionTitleText} transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
                    </button>
                  )}
                  {(open || isCollapsed) && (
                    <div className="space-y-0">
                      {section.items.map((item) => {
                    const { to, label, icon: Icon, countKey, bg, text, shortcut } = item;
                    // Color & bold the keyboard-shortcut letter inside the label
                    // (case-insensitive, first occurrence). Falls back to plain label.
                    const renderLabel = () => {
                      if (!shortcut) return label;
                      const idx = label.toLowerCase().indexOf(shortcut.toLowerCase());
                      if (idx < 0) return label;
                      return (
                        <>
                          {label.slice(0, idx)}
                          <span className={`font-bold ${text}`}>{label[idx]}</span>
                          {label.slice(idx + 1)}
                        </>
                      );
                    };
                    const link = (
                      <NavLink
                        key={to} to={to}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`nav-${label.toLowerCase().replace(/[\s/]/g, '-')}`}
                        className={({ isActive }) =>
                          `relative flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm transition-all duration-200 group ${
                            isActive ? navActiveCls : `${navInactiveText} ${navHoverBg}`
                          } ${isCollapsed ? 'lg:justify-center' : ''}`
                        }
                      >
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                          <Icon size={15} weight="duotone" className={text} />
                        </span>
                        <span className={`flex-1 truncate ${isCollapsed ? 'lg:hidden' : ''}`}>{renderLabel()}</span>
                        {countKey && counts[countKey] > 0 && (
                          <span className={`text-[10px] font-bold bg-[#F59E0B] text-white rounded-full min-w-[18px] text-center ${isCollapsed ? 'lg:absolute lg:top-1 lg:right-1 lg:px-1 lg:py-0' : 'px-1.5 py-0.5'}`}>{counts[countKey]}</span>
                        )}
                      </NavLink>
                    );
                    return isCollapsed ? (
                      <Tooltip key={to}>
                        <TooltipTrigger asChild>{link}</TooltipTrigger>
                        <TooltipContent side="right" className="bg-[#0A0A0A] text-white border-0 text-xs rounded-md">
                          {label}
                        </TooltipContent>
                      </Tooltip>
                    ) : link;
                  })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer */}
          <div className={`p-3 border-t ${headerBorder}`}>
            <div className={`flex items-center gap-2.5 px-1 ${isCollapsed ? 'lg:flex-col lg:gap-2 lg:px-0' : ''}`}>
              <div className="w-8 h-8 bg-[#3B82F6] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className={`min-w-0 flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}>
                <p className={`text-sm font-medium ${dark ? 'text-white' : 'text-[#0A0A0A]'} truncate`}>{user?.name}</p>
                <p className={`text-[10px] capitalize ${dark ? 'text-white/60' : 'text-[#A1A1AA]'}`}>{user?.role}</p>
              </div>
              <div className={dark ? 'text-white/70' : 'text-[#52525B]'}>
                <SidebarThemePicker
                  value={user?.sidebar_bg || ''}
                  onChange={handleThemeChange}
                  triggerSize="sm"
                  align="end"
                  testIdPrefix="sidebar-theme"
                />
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={handleLogout} className={`transition-colors ${dark ? 'text-white/60 hover:text-[#FCA5A5]' : 'text-[#A1A1AA] hover:text-[#EF4444]'}`} data-testid="logout-btn">
                    <SignOut size={16} />
                  </button>
                </TooltipTrigger>
                {isCollapsed && <TooltipContent side="right" className="bg-[#0A0A0A] text-white border-0 text-xs rounded-md">Sign out</TooltipContent>}
              </Tooltip>
            </div>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto">
          <TrialBanner />
          {/* Top bar with hamburger */}
          <div className="sticky top-0 z-20 flex items-center gap-3 bg-white/85 backdrop-blur border-b border-[#E4E4E7] px-3 py-2 lg:px-4">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#0A0A0A]"
              aria-label="Toggle sidebar"
              data-testid="toggle-sidebar-btn"
            >
              <List size={18} />
            </button>
            {currentPage && (() => {
              const Icon = currentPage.icon;
              return (
                <div className="hidden lg:flex items-center gap-3 ml-4" data-testid="topbar-page-header">
                  <div className={`w-8 h-8 rounded-lg ${currentPage.bg} flex items-center justify-center shrink-0`} data-testid="topbar-page-icon">
                    <Icon size={16} weight="duotone" className={currentPage.text} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2 flex-nowrap">
                      <span className="text-base font-bold tracking-tight text-[#0A0A0A] whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }} data-testid="topbar-page-title">{currentPage.title}</span>
                      <span className="text-[#D4D4D8]">|</span>
                      <span className="text-[11px] text-[#52525B] whitespace-nowrap">Today, {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    {currentPage.tagline && (
                      <p className="text-[10px] text-[#71717A] leading-snug truncate max-w-[520px]" data-testid="topbar-page-tagline">{currentPage.tagline}</p>
                    )}
                  </div>
                </div>
              );
            })()}
            <div className="flex items-center gap-2 lg:hidden">
              <div className="w-7 h-7 rounded-md flex items-center justify-center overflow-hidden bg-[#3B82F6]">
                {company.logo ? (
                  <img src={company.logo} alt="logo" className="w-full h-full object-contain bg-white" />
                ) : (
                  <Fingerprint size={14} weight="bold" className="text-white" />
                )}
              </div>
              <span className="text-sm font-bold tracking-tight text-[#0A0A0A] truncate" style={{ fontFamily: 'var(--font-heading)' }}>{company.name || 'SMSuitePro'}</span>
            </div>

            {/* Financial-year pill — sits on the far right of the topbar across every page */}
            {(() => {
              const fy = getCurrentFY();
              const fmtIN = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <div
                  className="ml-auto flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-[#F8FAFC] px-2.5 py-1 text-[10px] sm:text-[11px]"
                  data-testid="topbar-fy-badge"
                  title={`Financial Year ${fy.label} · ${fmtIN(fy.startDate)} → ${fmtIN(fy.endDate)}`}
                >
                  <span className="hidden sm:inline uppercase tracking-[0.18em] text-[9px] font-semibold text-[#52525B]">FY</span>
                  <span className="font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }} data-testid="topbar-fy-label">{fy.shortLabel}</span>
                  <span className="hidden md:inline text-[10px] text-[#A1A1AA]">·</span>
                  <span className="hidden md:inline text-[10px] text-[#52525B]">{fmtIN(fy.startDate)} → {fmtIN(fy.endDate)}</span>
                </div>
              );
            })()}
            <button
              type="button" onClick={() => setGuideOpen(true)}
              className="flex items-center gap-1.5 rounded-full bg-[#FFF7ED] border border-[#FED7AA] px-2.5 py-1 text-[11px] font-semibold text-[#C2410C] hover:bg-[#FFEDD5] transition-colors"
              data-testid="topbar-quick-start-btn" title="Quick Start Guide"
            >
              <BookOpen size={14} weight="duotone" /> <span className="hidden sm:inline">Quick Start Guide</span>
              {!guideOpen && hasUnseenUpdates() && <span className="ml-0.5 rounded-full bg-[#F97316] px-1.5 text-[9px] font-bold text-white" data-testid="topbar-new-updates-badge">NEW</span>}
            </button>
          </div>
          <QuickStartDialog open={guideOpen} onOpenChange={setGuideOpen} seenKey={isAdmin ? qsKey : null} inApp />
          <Outlet />
        </main>
        <KeyboardShortcuts />
      </div>
    </TooltipProvider>
  );
}
